import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLanguage } from '../App';
import { useAuth } from '../AuthContext';
import { t } from '../i18n';
import { motion, AnimatePresence } from 'framer-motion';
import { RotateCcw, Zap, Coins, TrendingUp, Award } from 'lucide-react';
import PetEvolutionPopup from '../components/game/PetEvolutionPopup';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ResultsPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { token, fetchUser } = useAuth();
  const data = location.state;
  const [saved, setSaved] = useState(false);
  const [confetti, setConfetti] = useState([]);
  const [gamiDelta, setGamiDelta] = useState(null);
  const [showXP, setShowXP] = useState(false);
  const [showEvolution, setShowEvolution] = useState(false);
  const [petEvolution, setPetEvolution] = useState(null);
  const isEN = language === 'en';

  useEffect(() => {
    if (!data) { navigate('/'); return; }
    const saveMatch = async () => {
      try {
        const headers = { 'Content-Type': 'application/json' };
        if (token) headers['Authorization'] = `Bearer ${token}`;
        const res = await fetch(`${BACKEND_URL}/api/matches`, {
          method: 'POST', headers,
          body: JSON.stringify({
            team1_name: data.team1Name, team2_name: data.team2Name,
            team1_score: data.team1Score, team2_score: data.team2Score,
            winner: data.winner, difficulty: data.difficulty,
            operations: data.operations, duration: data.duration,
            team1_correct: data.team1Correct, team1_wrong: data.team1Wrong,
            team2_correct: data.team2Correct, team2_wrong: data.team2Wrong,
            team1_best_streak: data.team1BestStreak || 0, team2_best_streak: data.team2BestStreak || 0,
            school_name: data.schoolName || '', subject: data.subjectId || '',
          })
        });
        if (res.ok) {
          const result = await res.json();
          setSaved(true);
          if (result.gamification) {
            setGamiDelta(result.gamification);
            setTimeout(() => setShowXP(true), 800);
            // Check for pet evolution
            if (result.gamification.pet_update?.evolved) {
              const pu = result.gamification.pet_update;
              // Fetch pet info for the popup
              if (token) {
                fetch(`${BACKEND_URL}/api/pet/me`, { headers: { Authorization: `Bearer ${token}` } })
                  .then(r => r.json())
                  .then(petData => {
                    if (petData?.has_pet) {
                      setPetEvolution({ petType: petData.pet_type, petName: petData.pet_name, oldStage: pu.new_stage - 1, newStage: pu.new_stage });
                      setTimeout(() => setShowEvolution(true), 2000);
                    }
                  }).catch(() => {});
              }
            }
          }
        }
        if (token) fetchUser();
      } catch (err) { console.error('Failed to save match:', err); }
    };
    saveMatch();
    const particles = Array.from({ length: 40 }, (_, i) => ({
      id: i, x: Math.random() * 100, delay: Math.random() * 2,
      duration: 2 + Math.random() * 3,
      color: ['#1a8cff', '#ff8c1a', '#ef4444', '#22c55e', '#eab308', '#8b5cf6'][Math.floor(Math.random() * 6)],
      size: 6 + Math.random() * 10,
    }));
    setConfetti(particles);
  }, []);

  if (!data) return null;

  const winnerName = data.winner === 'team1' ? data.team1Name : data.winner === 'team2' ? data.team2Name : null;
  const isDraw = data.winner === 'draw';
  const team1Accuracy = data.team1Correct + data.team1Wrong > 0 ? Math.round((data.team1Correct / (data.team1Correct + data.team1Wrong)) * 100) : 0;
  const team2Accuracy = data.team2Correct + data.team2Wrong > 0 ? Math.round((data.team2Correct / (data.team2Correct + data.team2Wrong)) * 100) : 0;

  // Rematch handler — reuse same settings
  const handleRematch = () => {
    navigate('/game', {
      state: {
        team1Name: data.team1Name, team2Name: data.team2Name,
        difficulty: data.difficulty, operations: data.operations,
        duration: data.duration, subjectId: data.subjectId,
        subjectType: data.subjectType || (data.subjectId === 'matematik' ? 'math' : 'mcq'),
        language: language, schoolName: data.schoolName || '',
      }
    });
  };

  return (
    <div className="results-page">
      <div className="confetti-container">
        {confetti.map(p => (
          <div key={p.id} className="confetti-piece" style={{
            left: `${p.x}%`, animationDelay: `${p.delay}s`, animationDuration: `${p.duration}s`,
            backgroundColor: p.color, width: `${p.size}px`, height: `${p.size}px`,
          }} />
        ))}
      </div>

      <motion.div className="results-content" initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 }}>
        {/* Winner Banner */}
        <motion.div className="winner-banner" initial={{ scale: 0.5 }} animate={{ scale: 1 }} transition={{ type: 'spring', stiffness: 200, damping: 12 }}>
          {isDraw ? (
            <>
              <div className="winner-trophy">&#129309;</div>
              <h1 className="winner-text" data-testid="results-winner-text">{t('draw', language)}</h1>
            </>
          ) : (
            <>
              <div className="winner-trophy">&#127942;</div>
              <h1 className="winner-text" data-testid="results-winner-text">{winnerName}</h1>
              <h2 className="winner-subtitle">{t('wins', language)}</h2>
            </>
          )}
        </motion.div>

        {/* XP & Coins Animation */}
        <AnimatePresence>
          {showXP && gamiDelta && (
            <motion.div className="results-gami-popup" data-testid="results-xp-popup"
              initial={{ opacity: 0, y: 20, scale: 0.8 }} animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0 }} transition={{ type: 'spring', stiffness: 200 }}>
              <div className="results-gami-row">
                <div className="results-gami-item results-gami-xp">
                  <Zap size={18} /> <span>+{gamiDelta.xp_gain} XP</span>
                </div>
                <div className="results-gami-item results-gami-coins">
                  <Coins size={18} /> <span>+{gamiDelta.coins_gain}</span>
                </div>
              </div>
              {gamiDelta.level_up && (
                <motion.div className="results-level-up" initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.3, type: 'spring' }}>
                  <TrendingUp size={20} />
                  <span>{isEN ? `LEVEL UP! Level ${gamiDelta.level}` : `NAIK LEVEL! Level ${gamiDelta.level}`}</span>
                </motion.div>
              )}
              {gamiDelta.is_personal_best && (
                <motion.div className="results-personal-best" initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.5, type: 'spring' }}>
                  <Award size={18} />
                  <span>{isEN ? 'NEW PERSONAL BEST!' : 'REKOD PERIBADI BARU!'}</span>
                </motion.div>
              )}
              {gamiDelta.percentile > 0 && (
                <div className="results-percentile">
                  {isEN ? `You're better than ${gamiDelta.percentile}% of players!` : `Anda lebih baik dari ${gamiDelta.percentile}% pemain!`}
                </div>
              )}
              {gamiDelta.quests_completed && gamiDelta.quests_completed.length > 0 && (
                <div className="results-quest-done">
                  {isEN ? `${gamiDelta.quests_completed.length} quest(s) completed!` : `${gamiDelta.quests_completed.length} misi selesai!`}
                </div>
              )}
              {gamiDelta.current_streak > 0 && (
                <div className="results-streak-info">
                  🔥 {isEN ? `${gamiDelta.current_streak} day streak!` : `Streak ${gamiDelta.current_streak} hari!`}
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Score Cards */}
        <div className="results-scores">
          <div className={`results-team-card results-team1-card ${data.winner === 'team1' ? 'results-winner-card' : ''}`}>
            <h3 className="results-team-name">{data.team1Name}</h3>
            <div className="results-big-score" data-testid="results-team1-final-score">{data.team1Score}</div>
            <div className="results-stats">
              <div className="results-stat"><span className="results-stat-label">{t('correct', language)}</span><span className="results-stat-value results-stat-correct">{data.team1Correct}</span></div>
              <div className="results-stat"><span className="results-stat-label">{t('wrong', language)}</span><span className="results-stat-value results-stat-wrong">{data.team1Wrong}</span></div>
              <div className="results-stat"><span className="results-stat-label">{t('accuracy', language)}</span><span className="results-stat-value">{team1Accuracy}%</span></div>
            </div>
          </div>
          <div className="results-vs">{t('vs', language)}</div>
          <div className={`results-team-card results-team2-card ${data.winner === 'team2' ? 'results-winner-card' : ''}`}>
            <h3 className="results-team-name">{data.team2Name}</h3>
            <div className="results-big-score" data-testid="results-team2-final-score">{data.team2Score}</div>
            <div className="results-stats">
              <div className="results-stat"><span className="results-stat-label">{t('correct', language)}</span><span className="results-stat-value results-stat-correct">{data.team2Correct}</span></div>
              <div className="results-stat"><span className="results-stat-label">{t('wrong', language)}</span><span className="results-stat-value results-stat-wrong">{data.team2Wrong}</span></div>
              <div className="results-stat"><span className="results-stat-label">{t('accuracy', language)}</span><span className="results-stat-value">{team2Accuracy}%</span></div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="results-actions">
          {/* REMATCH - Most prominent */}
          <motion.button className="results-btn results-btn-rematch" onClick={handleRematch}
            whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} data-testid="results-rematch-button">
            <RotateCcw size={20} />
            {isEN ? 'REMATCH!' : 'MAIN LAGI!'}
          </motion.button>

          <motion.button className="results-btn results-btn-whatsapp" onClick={() => {
            const msg = isDraw
              ? `${isEN ? "It's a draw" : 'Seri'}! ${data.team1Name} ${data.team1Score} - ${data.team2Score} ${data.team2Name} ${isEN ? 'on PANTASS!' : 'di PANTASS!'} ${isEN ? 'Come play!' : 'Jom main!'} https://pantass.com`
              : `${winnerName} ${isEN ? 'WINS' : 'MENANG'}! ${data.team1Name} ${data.team1Score} - ${data.team2Score} ${data.team2Name} ${isEN ? 'on PANTASS!' : 'di PANTASS!'} ${isEN ? 'Challenge us!' : 'Jom cabaran!'} https://pantass.com`;
            window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank');
          }} whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} data-testid="results-whatsapp-button">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="white"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
            {t('shareWhatsApp', language)}
          </motion.button>

          {!isDraw && (
            <motion.button className="results-btn results-btn-cert"
              onClick={() => navigate(`/certificate?name=${encodeURIComponent(winnerName)}&score=${data.winner === 'team1' ? data.team1Score : data.team2Score}&subject=PANTASS&date=${new Date().toLocaleDateString()}`)}
              whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              {isEN ? 'Get Certificate' : 'Dapat Sijil'}
            </motion.button>
          )}

          <motion.button className="results-btn results-btn-leaderboard" onClick={() => navigate('/leaderboard')}
            whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} data-testid="results-leaderboard-button">
            {t('leaderboard', language)}
          </motion.button>
          <button className="results-btn results-btn-home" onClick={() => navigate('/')}>
            {t('backToHome', language)}
          </button>
        </div>
      </motion.div>

      {/* Pet Evolution Popup */}
      {petEvolution && (
        <PetEvolutionPopup
          show={showEvolution}
          petType={petEvolution.petType}
          petName={petEvolution.petName}
          oldStage={petEvolution.oldStage}
          newStage={petEvolution.newStage}
          onClose={() => setShowEvolution(false)}
          isEN={isEN}
        />
      )}
    </div>
  );
}
